import * as React from "react";
import * as ReactDOM from "react-dom";
import { Icon } from "@grandvisionhq/icons";
import LoaderScreen from "../../Loader/Loader/LoaderScreen.js";
import { isEqualPositionsAndOrder } from "../../Util/Util/is-equal-positions-and-order.js";
import { useGoogleMapsModule } from "./external-module.js";
import { useGoogleMapsContext } from "../GoogleMapsContext/GoogleMaps/GoogleMapsContext/GoogleMapsContext.js";
import { getMarkerProps } from "../utils/GoogleMaps/utils/get-marker-props.js";
/* empty css                */
import { usePrevious } from "../../Util/Util/use-previous.js";
const MAX_MARKERS_AMOUNT = 50;
const DEFAULT_MAX_ZOOM = 16;
const DEFAULT_SHARE_CURRENT_LOCATION_LABEL = "Share current location";
const DEFAULT_ZOOM_IN_LABEL = "Zoom in";
const DEFAULT_ZOOM_OUT_LABEL = "Zoom out";
const GoogleMaps = ({
  showUseMyLocation = false,
  onClickUseMyLocation,
  userLocation,
  userLocationZoomLevel,
  centerOnUserLocation = false,
  highlightMarkerId,
  hovered,
  markers,
  onMarkerClick,
  onMarkerHover,
  onMapClick,
  mapOptions,
  showLoader = true,
  center: centerProp,
  markersUpdateConfig,
  labels,
  id
}) => {
  const {
    googleMapsSettings: {
      apiKey,
      defaultZoom,
      hasCustomZoomLevel,
      maxZoom,
      defaultCenter,
      pin,
      markerClusterOptions
    }
  } = useGoogleMapsContext();
  const mapRef = React.useRef();
  const [markerRef, setMarkerRef] = React.useState({});
  const [center, setCenter] = React.useState(centerProp ?? defaultCenter);
  const [zoom, setZoom] = React.useState(defaultZoom);
  const [userSharingLocation, setUserSharingLocation] = React.useState(false);
  const useMyLocationControlDiv = React.useRef();
  const zoomControlDiv = React.useRef();
  const prevMarkers = usePrevious(markers);
  const [ReactGoogleMaps] = useGoogleMapsModule();
  const options = {
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    maxZoom: maxZoom ?? DEFAULT_MAX_ZOOM,
    ...mapOptions,
    zoomControl: false
    // We use custom UI for zoom control, but still use mapOptions.zoomControl to show/hide the control
  };
  const markersWithMapBehavior = React.useMemo(
    () => markers.filter((marker) => !!!marker?.excludedFromMapBehavior),
    [markers]
  );
  const onMarkerLoad = (marker, markerId) => markerId && setMarkerRef((prevState) => ({ ...prevState, [markerId]: marker }));
  const handleZoomChanged = React.useCallback(
    () => mapRef.current && setZoom(mapRef?.current?.getZoom() ?? defaultZoom),
    [defaultZoom]
  );
  const onHover = React.useCallback(
    (markerId, markerRef2) => onMarkerHover && onMarkerHover(markerId, markerRef2[markerId]),
    [onMarkerHover]
  );
  const handleMapClick = React.useCallback(
    (e) => onMapClick && onMapClick(e),
    [onMapClick]
  );
  const handleZoomInClick = React.useCallback(() => {
    const currentZoom = mapRef?.current?.getZoom() ?? 0;
    mapRef.current?.setZoom(currentZoom + 1);
  }, []);
  const handleZoomOutClick = React.useCallback(() => {
    const currentZoom = mapRef?.current?.getZoom() ?? 0;
    mapRef.current?.setZoom(currentZoom - 1);
  }, []);
  const handleUseMyLocationClick = React.useCallback(() => {
    onClickUseMyLocation && onClickUseMyLocation();
    userLocation && mapRef.current && handleCurrentPositionClick(userLocation);
  }, [onClickUseMyLocation, userLocation]);
  const handleCurrentPositionClick = (userLocation2) => {
    if (!userLocation2 || !mapRef.current) {
      return;
    }
    mapRef.current.panTo(userLocation2);
    setZoom(14);
  };
  const handleMarkerClick = React.useCallback(
    (markerId) => {
      if (!markerId) {
        return;
      }
      const highlightMarkerIdPos = markers.find(
        (marker) => marker.markerId === markerId
      );
      if (mapRef.current && highlightMarkerIdPos) {
        mapRef.current.panTo(highlightMarkerIdPos.position);
      }
      onMarkerClick && onMarkerClick(markerId);
    },
    [markers, onMarkerClick]
  );
  const renderMarkers = (Marker, clusterer) => markers.filter((marker) => !(marker.position.lat === 0 && marker.position.lng === 0)).map((marker, index) => /* @__PURE__ */ React.createElement(
    Marker,
    {
      title: marker.title,
      key: `${marker.markerId}-${index}`,
      position: marker.position,
      onClick: () => handleMarkerClick(marker.markerId),
      clusterer,
      onLoad: (mapsMarker) => onMarkerLoad(mapsMarker, marker.markerId),
      onMouseOver: () => !marker.inactive && onHover(marker.markerId, markerRef),
      onMouseOut: () => onHover(null, markerRef),
      ...getMarkerProps({
        highlight: marker.markerId === highlightMarkerId,
        hovered: !marker.inactive && marker.markerId === hovered,
        inactive: marker.inactive,
        isUserMarker: marker.isUserLocation,
        icon: marker.pin || pin,
        infoWindow: marker.infoWindow
      })
    }
  ));
  const fitMarkers = React.useCallback(() => {
    if (!markersWithMapBehavior.length || markersWithMapBehavior.length > MAX_MARKERS_AMOUNT || hasCustomZoomLevel) {
      setZoom(defaultZoom);
      setCenter(center ?? defaultCenter);
    } else if (mapRef.current) {
      const bounds = markersWithMapBehavior.reduce(
        (newBounds, marker) => newBounds.extend(marker.position),
        new google.maps.LatLngBounds()
      );
      mapRef.current?.fitBounds(bounds);
    }
  }, [center, defaultCenter, defaultZoom, markersWithMapBehavior, hasCustomZoomLevel]);
  const setupControls = React.useCallback(() => {
    if (mapOptions?.zoomControl !== false && zoomControlDiv.current) {
      mapRef.current?.controls[google.maps.ControlPosition.RIGHT_BOTTOM]?.push(
        zoomControlDiv.current
      );
    }
    if (showUseMyLocation && onClickUseMyLocation && useMyLocationControlDiv.current) {
      mapRef.current?.controls[google.maps.ControlPosition.TOP_RIGHT]?.push(
        useMyLocationControlDiv.current
      );
    }
  }, [
    showUseMyLocation,
    onClickUseMyLocation,
    useMyLocationControlDiv,
    mapOptions?.zoomControl,
    zoomControlDiv
  ]);
  const onLoad = React.useCallback(
    (map) => {
      mapRef.current = map;
      setupControls();
      fitMarkers();
    },
    [setupControls, fitMarkers]
  );
  React.useEffect(() => setZoom(defaultZoom), [defaultZoom]);
  React.useEffect(() => centerProp && setCenter(centerProp), [centerProp]);
  React.useEffect(() => {
    const activeUserLocation = Boolean(userLocation);
    if (activeUserLocation && userSharingLocation !== activeUserLocation) {
      setUserSharingLocation(true);
    } else if (!activeUserLocation) {
      setUserSharingLocation(false);
    }
  }, [userLocation, userSharingLocation]);
  React.useEffect(() => {
    if (centerOnUserLocation && userLocation && userSharingLocation) {
      const zoom2 = userLocationZoomLevel ?? defaultZoom;
      setZoom(zoom2);
      setCenter(userLocation);
    } else if (markersWithMapBehavior.length && (!prevMarkers || !isEqualPositionsAndOrder(prevMarkers, markers))) {
      if (markersUpdateConfig && Object.keys(markersUpdateConfig).length > 0) {
        if (markersUpdateConfig.centerToFirst && markersWithMapBehavior[0]) {
          setCenter(markersWithMapBehavior[0].position);
        }
        if (markersUpdateConfig.zoom !== void 0 && !hasCustomZoomLevel) {
          mapRef.current?.setZoom(markersUpdateConfig.zoom);
          setZoom(markersUpdateConfig.zoom);
        }
      } else {
        fitMarkers();
      }
    }
  }, [
    prevMarkers,
    userLocationZoomLevel,
    centerOnUserLocation,
    defaultZoom,
    markers,
    markersWithMapBehavior,
    fitMarkers,
    markersUpdateConfig,
    userSharingLocation
  ]);
  React.useEffect(() => {
    useMyLocationControlDiv.current = document.createElement("div");
    zoomControlDiv.current = document.createElement("div");
  }, []);
  const [ready, setReady] = React.useState(false);
  const scriptLoaded = React.useCallback(() => setReady(true), []);
  if (!ReactGoogleMaps) {
    return showLoader ? /* @__PURE__ */ React.createElement(LoaderScreen, null) : null;
  }
  const shouldRenderUseMyLocationButton = showUseMyLocation && onClickUseMyLocation;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    ReactGoogleMaps.LoadScript,
    {
      googleMapsApiKey: apiKey,
      onLoad: scriptLoaded,
      loadingElement: /* @__PURE__ */ React.createElement(LoaderScreen, null)
    },
    /* @__PURE__ */ React.createElement(
      ReactGoogleMaps.GoogleMap,
      {
        "data-t": "google-map",
        mapContainerClassName: "google-maps__container",
        options,
        onLoad,
        onClick: handleMapClick,
        onZoomChanged: handleZoomChanged,
        zoom,
        center,
        id
      },
      ready && (markerClusterOptions ? /* @__PURE__ */ React.createElement(ReactGoogleMaps.MarkerClusterer, { ...markerClusterOptions }, (clusterer) => /* @__PURE__ */ React.createElement(React.Fragment, null, renderMarkers(ReactGoogleMaps.Marker, clusterer))) : renderMarkers(ReactGoogleMaps.Marker))
    )
  ), mapOptions?.zoomControl !== false && zoomControlDiv.current && ReactDOM.createPortal(
    /* @__PURE__ */ React.createElement("div", { className: "google-maps__zoom-control" }, /* @__PURE__ */ React.createElement(
      "button",
      {
        className: "google-maps__zoom-in-button",
        onClick: handleZoomInClick,
        title: labels?.zoomIn || DEFAULT_ZOOM_IN_LABEL,
        "aria-label": labels?.zoomIn || DEFAULT_ZOOM_IN_LABEL
      },
      /* @__PURE__ */ React.createElement(Icon, { name: "plus" })
    ), /* @__PURE__ */ React.createElement(
      "button",
      {
        className: "google-maps__zoom-out-button",
        onClick: handleZoomOutClick,
        title: labels?.zoomOut || DEFAULT_ZOOM_OUT_LABEL,
        "aria-label": labels?.zoomOut || DEFAULT_ZOOM_OUT_LABEL
      },
      /* @__PURE__ */ React.createElement(Icon, { name: "minus" })
    )),
    zoomControlDiv.current
  ), shouldRenderUseMyLocationButton && useMyLocationControlDiv.current && ReactDOM.createPortal(
    /* @__PURE__ */ React.createElement(
      "button",
      {
        className: "google-maps__center-position-button",
        onClick: handleUseMyLocationClick,
        title: labels?.shareCurrentLocation || DEFAULT_SHARE_CURRENT_LOCATION_LABEL,
        "aria-label": labels?.shareCurrentLocation || DEFAULT_SHARE_CURRENT_LOCATION_LABEL
      },
      /* @__PURE__ */ React.createElement(Icon, { name: "gps" })
    ),
    useMyLocationControlDiv.current
  ));
};
export {
  GoogleMaps as default
};
//# sourceMappingURL=GoogleMaps.js.map
